import { loadStripe, type StripeAddressElementChangeEvent } from "@stripe/stripe-js";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

export type StripeAddressValue = {
  name?: string;
  firstName?: string;
  lastName?: string;
  address_line1?: string;
  address_line2?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
  address_country?: string;
};

export type StripeAddressChangeEvent = {
  complete: boolean;
  value: StripeAddressValue;
};

export type StripeAddressElementHandle = {
  triggerValidation: () => Promise<void>;
  getValue: () => Promise<StripeAddressChangeEvent | null>;
  isValid: () => boolean;
};

const getEnv = (): { stripeKey: string; gMapsKey?: string } => {
  const w = window as any;
  const env = w?.Penmate?.env;
  const stripeKey = typeof env?.stripeKey === "string" ? env.stripeKey : "";
  const gMapsKey = typeof env?.gMapsKey === "string" ? env.gMapsKey : undefined;
  if (!stripeKey) throw new Error("Penmate.env.stripeKey is not available");
  return { stripeKey, gMapsKey };
};

const ALLOWED_COUNTRIES = [
  // Copy exactly from legacy `stripe-address.component.ts`
  "US",
  "GB",
  "DE",
  "CA",
  "MX",
  "FR",
  "AU",
  "ES",
  "PL",
  "CH",
  "AT",
  "DO",
  "BR",
  "AM",
  "TR",
  "CO",
  "DK",
  "EE",
  "BE",
  "IE",
  "KR",
  "MW",
  "NZ",
  "IT",
  "NL",
  "PT",
  "SE",
  "NO",
  "FI",
  "AR",
  "CL",
  "IN",
  "JP",
  "SG",
  "HK",
] as const;

const toCountry = (v: StripeAddressValue | null | undefined): string => {
  const c = v?.address_country;
  return typeof c === "string" && c ? c.toUpperCase() : "US";
};

const pickNameParts = (v: StripeAddressValue | null | undefined): { firstName: string; lastName: string } => {
  const firstName = typeof v?.firstName === "string" ? v.firstName : "";
  const lastName = typeof v?.lastName === "string" ? v.lastName : "";
  if (firstName || lastName) return { firstName, lastName };
  const name = typeof v?.name === "string" ? v.name.trim() : "";
  if (!name) return { firstName: "", lastName: "" };
  const [first, second] = name.split(/\s+/, 2);
  return { firstName: first || "", lastName: second || "" };
};

const normalizeFromStripe = (evt: StripeAddressElementChangeEvent | null | undefined): StripeAddressValue => {
  const value = (evt as any)?.value;
  if (!value || typeof value !== "object") return {};
  const address = (value as any)?.address;
  return {
    name: typeof (value as any)?.name === "string" ? (value as any).name : undefined,
    firstName: typeof (value as any)?.firstName === "string" ? (value as any).firstName : undefined,
    lastName: typeof (value as any)?.lastName === "string" ? (value as any).lastName : undefined,
    address_line1: typeof address?.line1 === "string" ? address.line1 : undefined,
    address_line2: typeof address?.line2 === "string" ? address.line2 : undefined,
    address_city: typeof address?.city === "string" ? address.city : undefined,
    address_state: typeof address?.state === "string" ? address.state : undefined,
    address_zip: typeof address?.postal_code === "string" ? address.postal_code : undefined,
    address_country: typeof address?.country === "string" ? address.country : undefined,
  };
};

const validateName = (value: StripeAddressValue): { valid: boolean; error: string } => {
  const firstName = typeof value.firstName === "string" ? value.firstName.trim() : "";
  const lastName = typeof value.lastName === "string" ? value.lastName.trim() : "";
  if (!firstName && !lastName) return { valid: true, error: "" };

  const firstNameValid = !firstName || firstName.length >= 2;
  const lastNameValid = !lastName || lastName.length >= 2;

  if (firstNameValid && lastNameValid) return { valid: true, error: "" };
  if (!firstNameValid && !lastNameValid) return { valid: false, error: "Please enter a complete first and last name" };
  if (!firstNameValid) return { valid: false, error: "Please enter a complete first name" };
  return { valid: false, error: "Please enter a complete last name" };
};

export const StripeAddressElement = forwardRef<StripeAddressElementHandle, {
  defaultAddress: StripeAddressValue;
  debounceMs?: number;
  onChange?: (event: StripeAddressChangeEvent) => void;
}>(function StripeAddressElement(props, ref) {
  const { defaultAddress, debounceMs = 300, onChange } = props;
  const mountRef = useRef<HTMLDivElement | null>(null);
  const elementRef = useRef<any | null>(null);
  const changeHandlerRef = useRef<((evt: any) => void) | null>(null);
  const debounceTimerRef = useRef<number | null>(null);
  const cachedValidRef = useRef(false);
  const [nameValidationError, setNameValidationError] = useState("");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const defaultValues = useMemo(() => {
    const { firstName, lastName } = pickNameParts(defaultAddress);
    const country = toCountry(defaultAddress);
    return {
      firstName,
      lastName,
      address: {
        line1: defaultAddress.address_line1,
        line2: defaultAddress.address_line2,
        city: defaultAddress.address_city,
        state: defaultAddress.address_state,
        postal_code: defaultAddress.address_zip,
        country,
      },
    };
  }, [defaultAddress]);

  const init = useCallback(async () => {
    if (!mountRef.current) return;
    setLoadError(null);
    setReady(false);

    let stripeKey = "";
    let gMapsKey: string | undefined;
    try {
      const env = getEnv();
      stripeKey = env.stripeKey;
      gMapsKey = env.gMapsKey;
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
      return;
    }

    let stripe: any = null;
    try {
      stripe = await loadStripe(stripeKey);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
      return;
    }
    if (!stripe) {
      setLoadError("Stripe failed to initialize");
      return;
    }

    const existing = elementRef.current;
    if (existing) {
      try {
        existing.destroy();
      } catch {
        // ignore
      }
      elementRef.current = null;
    }

    const country = toCountry(defaultAddress);
    const isDefaultUS = country === "US";

    const elements = stripe.elements();
    const addressElement = (elements.create as any)("address", {
      mode: "billing",
      display: {
        name: "split",
        firstName: "ALWAYS",
        lastName: "ALWAYS",
        country: isDefaultUS ? "AUTO" : "ALWAYS",
        addressLine1: "ALWAYS",
        addressLine2: "AUTO",
        city: "ALWAYS",
        state: "AUTO",
        postalCode: "AUTO",
      },
      allowedCountries: [...ALLOWED_COUNTRIES],
      fields: {
        phone: "never",
        enforceRequiredFields: "always",
        firstName: "always",
        lastName: "always",
      },
      validation: { enforceRequiredFields: true },
      autocomplete: gMapsKey
        ? {
            mode: "google_maps_api",
            apiKey: gMapsKey,
            autocompleteOptions: { types: ["address"], componentRestrictions: {} },
          }
        : undefined,
      defaultValues,
    });

    addressElement.mount(mountRef.current);
    elementRef.current = addressElement;
    setReady(true);

    const handleChange = (evt: any) => {
      if (!onChange) return;
      if (debounceTimerRef.current) window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = window.setTimeout(() => {
        const mapped = normalizeFromStripe(evt);
        const nameValidation = validateName(mapped);
        setNameValidationError(nameValidation.error);
        cachedValidRef.current = !!evt?.complete && nameValidation.valid;
        onChange({ complete: !!evt?.complete, value: mapped });
      }, debounceMs);
    };
    changeHandlerRef.current = handleChange;
    addressElement.on("change", handleChange);
    addressElement.on("blur", async () => {
      try {
        await addressElement.getValue();
      } catch {
        // ignore
      }
    });
  }, [debounceMs, defaultAddress, defaultValues, onChange]);

  useEffect(() => {
    cachedValidRef.current = false;
    setNameValidationError("");
    void init();
    return () => {
      if (debounceTimerRef.current) window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
      const el = elementRef.current;
      if (el) {
        try {
          if (changeHandlerRef.current) el.off("change", changeHandlerRef.current);
          el.destroy();
        } catch {
          // ignore
        }
      }
      elementRef.current = null;
      changeHandlerRef.current = null;
    };
  }, [init]);

  useImperativeHandle(
    ref,
    () => ({
      triggerValidation: async () => {
        const el = elementRef.current;
        if (!el) return;
        await el.getValue();
      },
      getValue: async () => {
        const el = elementRef.current;
        if (!el) return null;
        const v = await el.getValue();
        if (!v) return null;
        const mapped = normalizeFromStripe(v);
        const nameValidation = validateName(mapped);
        setNameValidationError(nameValidation.error);
        cachedValidRef.current = !!v?.complete && nameValidation.valid;
        return { complete: !!v?.complete && nameValidation.valid, value: mapped };
      },
      isValid: () => cachedValidRef.current,
    }),
    [],
  );

  return (
    <div>
      <div ref={mountRef} style={{ minHeight: 220 }} />
      {!ready && !loadError ? (
        <div className="mt-2 text-xs text-muted-foreground">Loading address form…</div>
      ) : null}
      {loadError ? (
        <div className="mt-2 text-xs text-destructive">
          Unable to load address form. {loadError}
        </div>
      ) : null}
      {nameValidationError ? (
        <div className="mt-2 text-xs text-destructive">{nameValidationError}</div>
      ) : null}
    </div>
  );
});
